import {
  Directive,
  ElementRef,
  PLATFORM_ID,
  afterNextRender,
  inject,
  input,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { animate, inView } from 'motion';

/**
 * Scroll-triggered reveal used across every rebuilt page instead of scattered
 * `animate-fade-in-up` classes. One motion vocabulary, one place to tune it.
 *
 * <div appReveal>...</div>
 * <div appReveal="rise" [revealDelay]="0.1">...</div>
 */
@Directive({
  selector: '[appReveal]',
  standalone: true,
})
export class Reveal {
  private el = inject(ElementRef<HTMLElement>);
  private platformId = inject(PLATFORM_ID);

  variant = input<'rise' | 'fade' | 'scale', string>('rise', {
    alias: 'appReveal',
    transform: (v) => (v === 'fade' || v === 'scale' ? v : 'rise'),
  });
  revealDelay = input<number>(0);

  constructor() {
    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) return;

      const node = this.el.nativeElement;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reduced) {
        node.style.opacity = '1';
        return;
      }

      const to: Record<string, unknown> =
        this.variant() === 'scale' ? { opacity: 1, scale: 1 } : { opacity: 1, y: 0 };

      node.style.opacity = '0';
      if (this.variant() === 'scale') node.style.transform = 'scale(0.94)';
      else if (this.variant() === 'rise') node.style.transform = 'translateY(28px)';

      inView(
        node,
        () => {
          animate(node, to as never, {
            duration: 0.8,
            delay: this.revealDelay(),
            ease: [0.22, 1, 0.36, 1],
          });
        },
        { amount: 0.2 },
      );
    });
  }
}
